import type {
  OffPagePage,
  OffPageSiteAnswers,
  OffPageSiteDetail,
  OffPageSites,
  OmsListings,
  SiteKind,
} from "@crux/contracts";
import { z } from "zod";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Off-page: other people's sites the answers cite, the pages on them, and the
 * page check that reads those pages for brand names.
 */

/** Every argument that changes what comes back, so a cache key can hold it. */
interface OffPageQuery {
  readonly from: string;
  readonly to: string;
  readonly engine?: string;
  readonly limit: number;
  readonly offset: number;
}

/**
 * Narrowing and order by OMS's own figures. The server applies them, because
 * OMS is not in the database; absent means no bound and the list's own order.
 */
export interface OmsQuery {
  readonly inOms?: "1";
  readonly minDa?: number;
  readonly minTraffic?: number;
  readonly maxSpam?: number;
  readonly maxPrice?: number;
  readonly sort?: "gap" | "da" | "traffic" | "organic" | "price";
  readonly dir?: "asc" | "desc";
}

export async function fetchOffPageSites(
  workspaceId: string,
  reportId: string,
  query: OffPageQuery &
    OmsQuery & {
      readonly sites: OffPageSiteView;
      readonly q?: string;
      readonly category?: SiteKindCategory;
    },
): Promise<OffPageSites> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/off-page/sites",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) throw new Error(problem(response.status, "load these sites"));
  return data;
}

/** One off-page site: who its answers name, and its pages. */
export async function fetchOffPageSite(
  workspaceId: string,
  reportId: string,
  query: OffPageQuery & { readonly domain: string },
): Promise<OffPageSiteDetail> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/off-page/site",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) throw new Error(problem(response.status, "load this site"));
  return data;
}

/** The answers that cited one exact page. */
export async function fetchOffPagePage(
  workspaceId: string,
  reportId: string,
  query: OffPageQuery & { readonly url: string; readonly show: "missing" | "all" },
): Promise<OffPagePage> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/off-page/page",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined) throw new Error(problem(response.status, "load this page"));
  return data;
}

/** Every answer that cited one site, with the site's pages each one cited. */
export async function fetchOffPageSiteAnswers(
  workspaceId: string,
  reportId: string,
  query: OffPageQuery & { readonly domain: string; readonly show: "missing" | "all" },
): Promise<OffPageSiteAnswers> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/off-page/site/answers",
    { params: { path: { workspaceId, reportId }, query } },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "load the answers citing this site"));
  return data;
}

/**
 * What OMS lists for these sites. A 502 is OMS not answering, which the screen
 * says in its own words rather than as an error about Crux.
 */
export async function fetchOmsListings(
  workspaceId: string,
  domains: readonly string[],
): Promise<OmsListings> {
  const { data, response } = await api.GET("/api/workspaces/{workspaceId}/off-page/oms", {
    params: { path: { workspaceId }, query: { domains: domains.join(",") } },
  });
  if (data !== undefined) return data;
  if (response.status === 502) {
    throw new Error(
      "OMS did not answer, so DA and price are missing. Try again in a few minutes.",
    );
  }
  throw new Error(problem(response.status, "load OMS figures"));
}

/** The sheet's query: the table's own filter and order, with no paging. */
export type OffPageSheetQuery = Omit<OffPageQuery, "limit" | "offset"> &
  OmsQuery & {
    readonly sites: OffPageSiteView;
    readonly q?: string;
    readonly category?: SiteKindCategory;
  };

/** The one field of the API's error body a refusal is read for. */
const refusalSchema = z.object({ message: z.string() });

/**
 * Every site the table shows, as an Excel workbook. A Rust-only route, so it
 * is not in the generated client.
 *
 * Fetched, not linked: a view too large for one sheet is a 400 whose message
 * says how to narrow it, and a plain link would land the reader on that JSON.
 */
export async function fetchOffPageSheet(
  workspaceId: string,
  reportId: string,
  query: OffPageSheetQuery,
): Promise<Blob> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const path = `/api/workspaces/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(reportId)}/off-page/sheet`;
  const response = await fetch(
    new URL(`${path}?${params.toString()}`, window.location.origin),
    { credentials: "same-origin" },
  );
  if (response.ok) return await response.blob();
  if (response.status === 400) {
    throw new Error(refusalSchema.parse(await response.json()).message);
  }
  throw new Error(problem(response.status, "download the sheet"));
}

export type SiteKindCategory = Extract<SiteKind, { state: "categorised" }>["category"];

export type OffPageSiteView = "usable" | "publishers" | "undecided" | "all";
